import type { FC } from 'react'
import { FaBookOpen, FaChevronLeft, FaChevronRight } from 'react-icons/fa6'

export type DuaHeaderCardProps = {
  /** The formatted title of the dua to display in the header card */
  title: string
  /** The current active page number */
  page: number
  /** The total number of pages for this dua */
  maxPage: number
  /** Callback fired when the previous page button is pressed */
  onPreviousPage: () => void
  /** Callback fired when the next page button is pressed */
  onNextPage: () => void
  /** Whether the current page is the first page */
  isFirstPage: boolean
  /** Whether the current page is the last page */
  isLastPage: boolean
}

/**
 * Top header card for dua pages displaying the dua title, page progress, and navigation buttons.
 */
const DuaHeaderCard: FC<DuaHeaderCardProps> = ({
  title,
  page,
  maxPage,
  onPreviousPage,
  onNextPage,
  isFirstPage,
  isLastPage,
}) => {
  return (
    <div className="flex items-center justify-between p-3 sm:p-4 rounded-2xl bg-white dark:bg-[#0f1d28]/95 border border-slate-200 dark:border-[#1e3445]/60 shadow-sm mb-4">
      {/* Dua Title and Page Info */}
      <div className="flex items-center gap-3 min-w-0">
        <div
          aria-hidden="true"
          className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-teal-50 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 flex items-center justify-center shrink-0 border border-teal-200 dark:border-teal-500/20 shadow-inner"
        >
          <FaBookOpen className="text-lg sm:text-xl" />
        </div>
        <div className="min-w-0">
          <h1 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight truncate">
            {title}
            <span className="sr-only"> {page}. Sayfa</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">
            Sayfa {page} / {maxPage}
          </p>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-[#0a1622]/80 border border-slate-200 dark:border-slate-700/60 rounded-full shrink-0">
        <button
          type="button"
          onClick={onPreviousPage}
          disabled={isFirstPage}
          aria-label="Önceki Sayfa"
          title="Önceki Sayfa"
          className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/70 dark:hover:bg-slate-700/50 disabled:opacity-25 disabled:pointer-events-none transition-colors"
        >
          <FaChevronLeft className="text-xs sm:text-sm" />
        </button>
        <button
          type="button"
          onClick={onNextPage}
          disabled={isLastPage}
          aria-label="Sonraki Sayfa"
          title="Sonraki Sayfa"
          className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/70 dark:hover:bg-slate-700/50 disabled:opacity-25 disabled:pointer-events-none transition-colors"
        >
          <FaChevronRight className="text-xs sm:text-sm" />
        </button>
      </div>
    </div>
  )
}

export default DuaHeaderCard
